import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import TicketStatusStepper from "@/modules/racket_lab_admin/TicketStatusStepper";
import TicketQrPanel from "@/modules/racket_lab_admin/TicketQrPanel";
import TicketPhotosPanel from "@/modules/racket_lab_admin/TicketPhotosPanel";
import AuditTimeline from "@/modules/racket_lab_admin/AuditTimeline";
import useAdminApi from "@/hooks/useAdminApi";
import useAdminMutation from "@/hooks/useAdminMutation";
import useRLContext from "@/hooks/useRLContext";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";
import { TICKET_STATUS_LABEL_KEY, labelKeyFor } from "@/i18n/racketLabMaps";

/**
 * RLAdminTicketDetail — Sub-sprint B + SS-1 (Feb 2026) lifecycle align.
 *
 * Header + lifecycle stepper + audit timeline.
 *
 * SS-1 changes:
 *  • Fields now read the RL-A.6 ticket schema (customer_reference_label,
 *    equipment_reference_label, equipment_notes, string_pattern,
 *    tension_main / tension_cross, string_brand_label, capability_key,
 *    racket_lab_shop_id, racket_lab_operator_id, racket_lab_machine_id,
 *    payment_status). Legacy R3 fields (customer_full_name, racket_model,
 *    status_history) are no longer referenced from this admin surface.
 *  • Audit timeline reads `data.audit[]` instead of `data.status_history`.
 *  • Allowed actions for the stepper are fetched from the backend
 *    (`/allowed-actions`) so the UI never goes out of sync with the
 *    state-machine again.
 *  • `data-testid`s and i18n keys unchanged.
 */
const Field = ({ label, value, testId }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className="text-sm text-slate-800 break-all">{value ?? "—"}</span>
  </div>
);

const _tensionDisplay = (m, c) => {
  if (m == null && c == null) return null;
  return `${m ?? "—"} / ${c ?? "—"}`;
};

const RLAdminTicketDetail = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, error, reload } = useAdminApi(
    `/admin/racket-lab/tickets/${encodeURIComponent(id)}`,
    { skip: !id },
  );
  const { data: actionsData, reload: reloadActions } = useAdminApi(
    `/admin/racket-lab/tickets/${encodeURIComponent(id)}/allowed-actions`,
    { skip: !id },
  );
  const { run, loading: mutating } = useAdminMutation();

  // SS-6.1 — consumption movements for the linked inventory item.
  // Skipped for qa_tester (operational inventory outside QA surface).
  const { ctx: rlCtx } = useRLContext();
  const invItemId = data?.racket_lab_inventory_item_id;
  const { data: consData, reload: reloadConsumption } = useAdminApi(
    `/admin/racket-lab/inventory/${invItemId}/movements`,
    { skip: !invItemId || rlCtx?.profile === "qa_tester", params: { ticket_id: id } },
  );
  const consumption = (consData?.items || []).find((m) => m.movement_type === "consumption");

  const onAction = async (action) => {
    await run("post", `/admin/racket-lab/tickets/${encodeURIComponent(id)}/${action}`, {}).catch(() => {});
    reload();
    reloadActions();
    if (invItemId && rlCtx?.profile !== "qa_tester") reloadConsumption();
  };

  return (
    <div data-testid="rl-admin-ticket-detail-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />

      <button type="button" onClick={() => navigate("/admin/racket-lab/tickets")}
        data-testid="rl-ticket-detail-back"
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("tickets.detail.back")}
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">
          {t("tickets.detail.title")}
          {data?.is_qa_test ? (
            <span
              data-testid="rl-ticket-detail-qa-badge"
              className="ml-2 align-middle rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700"
            >
              {t("tickets.qa_badge")}
            </span>
          ) : null}
        </h1>
        <p className="text-xs text-slate-500">{t("tickets.detail.subtitle")}</p>
      </header>

      {loading && <div data-testid="rl-ticket-detail-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("tickets.detail.loading")}</div>}
      {!loading && error && (
        <div data-testid="rl-ticket-detail-error" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error.status === 404
            ? t("tickets.detail.not_found", { id: id?.slice(0, 18) ?? "" })
            : `${t("dashboard.error_prefix")} ${error.status || ""}: ${error.detail || t("dashboard.error_unknown")}`}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <section data-testid="rl-ticket-detail-card" className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-2">
            <Field label={t("tickets.detail.ticket_id")} value={data.id} testId="rl-ticket-field-id" />
            <Field label={t("tickets.columns.status")} value={t(labelKeyFor(TICKET_STATUS_LABEL_KEY, data.status))} testId="rl-ticket-field-status" />
            <Field label={t("tickets.detail.customer_demo")} value={data.customer_reference_label || data.customer_user_id} testId="rl-ticket-field-customer" />
            <Field label={t("tickets.detail.racket")} value={data.equipment_reference_label} testId="rl-ticket-field-racket" />
            <Field label={t("tickets.columns.shop")} value={data.racket_lab_shop_id} testId="rl-ticket-field-shop" />
            <Field label={t("tickets.detail.player_synthetic")} value={data.racket_lab_operator_id} testId="rl-ticket-field-player" />
            <Field label={t("common.labels.created_at")} value={data.created_at} testId="rl-ticket-field-created-at" />
            <Field label={t("tickets.detail.scheduled_for")} value={data.scheduled_for} testId="rl-ticket-field-scheduled-for" />
            <Field label={t("tickets.detail.notes")} value={data.equipment_notes || data.notes_internal} testId="rl-ticket-field-notes" />
          </section>

          {(data.equipment_product_type || data.equipment_brand_label || data.equipment_model_label ||
            data.equipment_accessory_label || data.string_main_label || data.string_cross_label ||
            data.tension_main != null || data.tension_cross != null || data.string_origin ||
            data.created_for_profile_type) && (
            <section data-testid="rl-ticket-detail-stringing" className="rounded-md border border-slate-200 bg-white p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-700">{t("tickets.detail.stringing_title")}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field label={t("tickets.detail.product_type")} value={data.equipment_product_type ? t(`tickets.stringing.product_type_${data.equipment_product_type}`) : null} testId="rl-ticket-field-product-type" />
                <Field label={t("tickets.detail.brand")} value={data.equipment_brand_label} testId="rl-ticket-field-brand" />
                <Field label={t("tickets.detail.model")} value={data.equipment_model_label} testId="rl-ticket-field-model" />
                {data.equipment_accessory_label ? (
                  <Field label={t("tickets.detail.accessory")} value={data.equipment_accessory_label} testId="rl-ticket-field-accessory" />
                ) : null}
                <Field label={t("tickets.detail.string_main")} value={data.string_main_label} testId="rl-ticket-field-string-main" />
                <Field label={t("tickets.detail.string_cross")} value={data.string_cross_label} testId="rl-ticket-field-string-cross" />
                <Field
                  label={t("tickets.detail.tension")}
                  value={
                    data.tension_main != null || data.tension_cross != null
                      ? `${_tensionDisplay(data.tension_main, data.tension_cross)} ${data.tension_unit || "kg"}`
                      : null
                  }
                  testId="rl-ticket-field-tension"
                />
                <Field label={t("tickets.detail.string_origin")} value={data.string_origin ? t(`tickets.stringing.origin_${data.string_origin}`) : null} testId="rl-ticket-field-string-origin" />
                {data.string_supply_format ? (
                  <Field label={t("tickets.detail.supply_format")} value={t(`tickets.stringing.supply_${data.string_supply_format}`)} testId="rl-ticket-field-supply-format" />
                ) : null}
                {data.customer_string_notes ? (
                  <Field label={t("tickets.detail.customer_string_notes")} value={data.customer_string_notes} testId="rl-ticket-field-customer-string-notes" />
                ) : null}
                {data.racket_lab_inventory_item_id ? (
                  <Field label={t("tickets.detail.inventory_item")} value={data.racket_lab_inventory_item_id} testId="rl-ticket-field-inventory-item" />
                ) : null}
                {data.racket_lab_inventory_item_id && rlCtx?.profile !== "qa_tester" ? (
                  <Field
                    label={t("tickets.detail.inventory_consumption")}
                    value={consumption
                      ? `${consumption.delta} (${t("tickets.detail.inventory_consumed")})`
                      : t("tickets.detail.inventory_not_consumed")}
                    testId="rl-ticket-field-inventory-consumption"
                  />
                ) : null}
                <Field label={t("tickets.detail.profile_type")} value={data.created_for_profile_type ? t(`tickets.stringing.profile_${data.created_for_profile_type}`) : null} testId="rl-ticket-field-profile-type" />
              </div>
            </section>
          )}

          <TicketStatusStepper
            ticket={data}
            onAction={onAction}
            busy={mutating}
            allowedActions={actionsData?.allowed_actions}
          />

          <TicketQrPanel ticketId={data.id} />

          <TicketPhotosPanel ticketId={data.id} />

          <section data-testid="rl-ticket-audit-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("ticket_actions.audit_timeline_title")}</h2>
            <AuditTimeline entries={data.audit || []} dataTestId="rl-audit-timeline" />
          </section>
        </>
      )}
    </div>
  );
};

export default RLAdminTicketDetail;
